import { readFromCache, saveToCache } from '@/lib/utils/cache-utils';
import { appId } from '@/lib/constants';
import { DBCollection } from '@/lib/utils/constants-shared';
import { DBContext, StorageContext } from '@/lib/context';
import { use, useEffect, useState } from 'react';
import { insertItemToDB, updateItemToDB, removeItemFromDB } from '../utils/db-service';
import { getPendingUpload, removePendingUpload } from '../utils/offline-uploads';
import { removeFile, uploadFile } from '../utils/file-storage';

export interface SyncState {
    status: 'error' | 'syncing' | 'ready';
    itemsToSync: {
        collection: DBCollection;
        id: string;
        type: 'add' | 'edit' | 'delete';
        storageFields: string[];
    }[];
}

export function useOfflineSync() {
    const db = use(DBContext);
    const storage = use(StorageContext);
    const [state, setState] = useState<SyncState>();
    
    useEffect(() => {
        readFromCache<SyncState['itemsToSync']>(appId, 'syncItems').then(itemsToSync => {
            setState({status: 'ready', itemsToSync: itemsToSync || []});
        });
    }, []);

    function markForSync(collection: DBCollection, id: string, type: SyncState['itemsToSync'][0]['type'], storageFields: string[] = []) {
        const existingEntry = state.itemsToSync.find(item => item.collection === collection && item.id === id);
        const itemsToSync = state.itemsToSync.filter(item => item.collection !== collection || item.id !== id);
        if (type !== 'delete' || existingEntry?.type !== 'add') { // don't save the entry if the add hasn't been synced yet)
            itemsToSync.push({collection, id, type, storageFields});
        }
        setState({...state, itemsToSync});
        saveToCache(appId, 'syncItems', itemsToSync);
    }

    function resolveSync(collection: DBCollection, id: string) {
        const itemsToSync = state.itemsToSync.filter(item => item.collection !== collection || item.id !== id);
        setState({...state, itemsToSync});
        saveToCache(appId, 'syncItems', itemsToSync);
    }

    async function uploadPendingStorage(syncItem: SyncState['itemsToSync'][0], item: Item): Promise<Item> {
        const newItem = {...item};
        for (const storageField of syncItem.storageFields) {
            const tempId = newItem[storageField]?.tempId;
            if (tempId) {
                const {file, path} = await getPendingUpload(tempId);
                if (syncItem.type === 'delete')  {
                    await removeFile(storage, path);
                }
                else {
                    const url = await uploadFile(storage, file, path);
                    newItem[storageField] = {url};
                }
                await removePendingUpload(tempId);
            }
        }
        return newItem;
    }

    async function sync(dataSets: {[collection: string]: ItemData}) {
        setState({...state, status: 'syncing'});

        const syncResults = await Promise.allSettled(state.itemsToSync.map(async item => {
            const itemData = dataSets[item.collection];
            const itemToSave = itemData.items[itemData.map.get(item.id)];
            if (item.type === 'add') {
                const updatedItemToSave = await uploadPendingStorage(item, itemToSave);
                return insertItemToDB(db, item.collection, updatedItemToSave);
            }
            else if (item.type === 'edit') {
                const updatedItemToSave = await uploadPendingStorage(item, itemToSave);
                return updateItemToDB(db, item.collection, updatedItemToSave);
            }
            else if (item.type === 'delete') {
                await uploadPendingStorage(item, itemToSave);
                return removeItemFromDB(db, item.collection, item.id);
            }
        }));

        const itemsToSync = state.itemsToSync.filter((_, ndx) => syncResults[ndx].status !== 'fulfilled');
        const status = itemsToSync.length ? 'error' : 'ready';
        setState({status, itemsToSync});
        saveToCache(appId, 'syncItems', itemsToSync);
    }

    return {markForSync, resolveSync, state, sync};
}